import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { authAPI } from "../api/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Seed a demo user so testers can log in immediately
  useEffect(() => {
    const MOCK_USERS_KEY = "agri_mock_users";
    try {
      const users = JSON.parse(localStorage.getItem(MOCK_USERS_KEY)) || [];
      if (!users.find((u) => u.email === "demo@agrihealth.com")) {
        users.push({
          id: "u_demo",
          name: "Demo Farmer",
          email: "demo@agrihealth.com",
          password: "demo1234",
          role: "premium",
          plan: "premium",
          avatar: "D",
          phone: "+254 700 000000",
          location: "Nakuru, Kenya",
          joined: new Date().toISOString(),
        });
        localStorage.setItem(MOCK_USERS_KEY, JSON.stringify(users));
      }
    } catch {}
  }, []);

  // Restore session on mount
  useEffect(() => {
    const token = localStorage.getItem("agri_token");
    const cached = localStorage.getItem("agri_user");
    if (token && cached) {
      try { setUser(JSON.parse(cached)); } catch {}
    }
    setLoading(false);

    // Listen for forced logout (401 from interceptor)
    const handle = () => { setUser(null); };
    window.addEventListener("auth:logout", handle);
    return () => window.removeEventListener("auth:logout", handle);
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await authAPI.login({ email, password });
    const { token, user: u } = res.data;
    localStorage.setItem("agri_token", token);
    localStorage.setItem("agri_user", JSON.stringify(u));
    setUser(u);
    return u;
  }, []);

  const register = useCallback(async (data) => {
    const res = await authAPI.register(data);
    const { token, user: u } = res.data;
    localStorage.setItem("agri_token", token);
    localStorage.setItem("agri_user", JSON.stringify(u));
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("agri_token");
    localStorage.removeItem("agri_user");
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const res = await authAPI.me();
      const u = res.data.user;
      localStorage.setItem("agri_user", JSON.stringify(u));
      setUser(u);
      return u;
    } catch { logout(); }
  }, [logout]);

  const updateUser = useCallback((updates) => {
    setUser((prev) => {
      const updated = { ...prev, ...updates };
      localStorage.setItem("agri_user", JSON.stringify(updated));
      return updated;
    });
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
